// import { ReactComponent as IconInfo } from "@/assets/svgs/icon-ds-info.svg";
import Icon from '@/componentsDS/Icon/Icon';
import Loader from '@/componentsDS/Loader/Loader';
import { orderBy, uniqueId } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import MultiLayerSubTable from "./table-group";
import MultiLayerHeader from "./table-header";

const EmptyLayer = ({ emptyMessage }) => (
	<div className="ds-multi-layer-table__tr">
		<div className="ds-multi-layer-table__td ds-multi-layer-table__td--empty">
			<span className="ds-multi-layer-table__td__empty-icon">
				<Icon name="information-circle" style="outlined" size="medium" />
			</span>
			<span className="ds-multi-layer-table__td__empty-message">
				{emptyMessage}
			</span>
		</div>
	</div>
);

const LoadingLayer = () => (
	<div className="ds-multi-layer-table__tr">
		<div className="ds-multi-layer-table__td ds-multi-layer-table__td--empty">
			<span className="ds-multi-layer-table__td__empty-icon">
				<Loader />
			</span>
		</div>
	</div>
);

const RenderData = ({
	sortedGroupKeys,
	sortConfig,
	groupedData,
	headers,
	groupItemLabel,
	customGroupHeaderRender,
	sortData,
	rowSize,
	stickyColumns,
	collapsedGroups,
	toggleGroupCollapse,
}) => (
	<>
		{sortedGroupKeys.map((groupKey) => {
			const id = uniqueId("sub-table-");

			return (
				<MultiLayerSubTable
					key={id}
					groupName={groupKey}
					entries={
						sortConfig.secondaryKey
							? sortData(
									groupedData[groupKey],
									headers.find((header) => header.key === sortConfig.secondaryKey),
									sortConfig.secondaryDirection
								)
							: groupedData[groupKey]
					}
					headers={headers}
					groupItemLabel={groupItemLabel}
					customGroupHeaderRender={customGroupHeaderRender}
					utilities={{ sortData }}
					rowSize={rowSize}
					stickyColumns={stickyColumns}
					isCollapsed={collapsedGroups[groupKey]}
					onToggleCollapse={toggleGroupCollapse}
				/>
			)
		})}
	</>
);

const MultiLayerTable = ({
	data,
	headers,
	groupBy,
	emptyMessage,
	groupItemLabel,
	customGroupHeaderRender,
	rowSize,
	stickyColumns,
	loading,
	sortBy,
	id
}) => {

	const [collapsedGroups, setCollapsedGroups] = useState({});
	const [sortConfig, setSortConfig] = useState({
		primaryKey: groupBy,
		primaryDirection: "asc",
		secondaryKey: groupBy !== sortBy ? sortBy : null,
		secondaryDirection: "desc",
	});

	const [isTableCollapsed, setIsTableCollapsed] = useState(false);

	const toggleTableCollapse = useCallback(() => {
		setIsTableCollapsed((prevState) => !prevState);
	}, []);

	const toggleGroupCollapse = useCallback((groupName) => {
		setCollapsedGroups((prevState) => ({
			...prevState,
			[groupName]: !prevState[groupName],
		}));
	}, []);

	const collapseWidth = 56;
	const totalMinWidth = useMemo(() => {
		return headers.reduce((acc, header) => {
			const columnMinWidth = header.minWidth || 150;
			return acc + columnMinWidth;
		}, collapseWidth);
	}, [headers]);

	const getNestedValue = (obj, path) => {
		return path
			.split(".")
			.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
	};

	const groupedData = useMemo(() => {
		return data.reduce((acc, item) => {
			const groupKey = getNestedValue(item, groupBy);
			if (!acc[groupKey]) {
				acc[groupKey] = [];
			}
			acc[groupKey].push(item);
			return acc;
		}, {});
	}, [data, groupBy]);

	const sortData = useCallback((dataArray, header, direction = "asc") => {
		const sortPath = header.sortTarget || header.key;
		return orderBy(dataArray, [(item) => getNestedValue(item, sortPath)], [direction]);
	}, []);

	const handleSort = useCallback(
		(key) => {
			const header = headers.find((header) => header.key === key);
			if (!header || header.sortable === false) return;

			const toggleDirection = (direction) => (direction === "asc" ? "desc" : "asc");

			setSortConfig((prevConfig) => {
				if (key === prevConfig.primaryKey) {
					return {
						...prevConfig,
						primaryDirection: toggleDirection(prevConfig.primaryDirection),
						secondaryKey: null,
						secondaryDirection: "asc",
					};
				}

				if (key === prevConfig.secondaryKey) {
					return {
						...prevConfig,
						secondaryDirection: toggleDirection(prevConfig.secondaryDirection),
					};
				}

				return {
					...prevConfig,
					secondaryKey: key,
					secondaryDirection: "asc",
				};
			});
		},
		[headers]
	);

	const sortedGroupKeys = useMemo(() => {
		return Object.keys(groupedData).sort((a, b) => {
			const aValue = getNestedValue(groupedData[a][0], groupBy);
			const bValue = getNestedValue(groupedData[b][0], groupBy);

			if (aValue < bValue) return sortConfig.primaryDirection === "asc" ? -1 : 1;
			if (aValue > bValue) return sortConfig.primaryDirection === "asc" ? 1 : -1;
			return 0;
		});
	}, [groupedData, groupBy, sortConfig]);

	const RenderLayer = () => {
		if (loading) {
			return <LoadingLayer />;
		} else if (sortedGroupKeys.length === 0) {
			return <EmptyLayer emptyMessage={emptyMessage} />;
		} else {
			return (
				<RenderData
					sortedGroupKeys={sortedGroupKeys}
					sortConfig={sortConfig}
					groupedData={groupedData}
					headers={headers}
					groupItemLabel={groupItemLabel}
					customGroupHeaderRender={customGroupHeaderRender}
					sortData={sortData}
					rowSize={rowSize}
					stickyColumns={stickyColumns}
					collapsedGroups={collapsedGroups}
					toggleGroupCollapse={toggleGroupCollapse}
				/>
			);
		}
	}

	return (
		<div className="ds-multi-layer-table__overview">
			<div className="ds-multi-layer-table" style={{ minWidth: `${totalMinWidth}px` }}>
				<MultiLayerHeader
					headers={headers}
					sortConfig={sortConfig}
					onSort={handleSort}
					groupBy={groupBy}
					isTableCollapsed={isTableCollapsed}
					toggleTableCollapse={toggleTableCollapse}
					stickyColumns={stickyColumns}
					id={id}
				/>

				{!isTableCollapsed && <RenderLayer />}
			</div>
		</div>
	);
};

MultiLayerTable.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			width: PropTypes.string,
			sortTarget: PropTypes.string,
			render: PropTypes.func,
			sortable: PropTypes.bool,
		})
	).isRequired,
	groupBy: PropTypes.string.isRequired,
	emptyMessage: PropTypes.string,
	groupItemLabel: PropTypes.string,
	customGroupHeaderRender: PropTypes.func,
	rowSize: PropTypes.oneOf(["large", "small"]),
	stickyColumns: PropTypes.number,
	loading: PropTypes.bool,
	sortBy: PropTypes.string,
	id: PropTypes.string
};

RenderData.propTypes = {
	sortedGroupKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
	sortConfig: PropTypes.shape({
		primaryKey: PropTypes.string.isRequired,
		primaryDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
		secondaryKey: PropTypes.string,
		secondaryDirection: PropTypes.oneOf(['asc', 'desc']),
	}).isRequired,
	groupedData: PropTypes.objectOf(
		PropTypes.arrayOf(PropTypes.object)
	).isRequired,
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			width: PropTypes.string,
			sortTarget: PropTypes.string,
			render: PropTypes.func,
			sortable: PropTypes.bool,
		})
	).isRequired,
	groupItemLabel: PropTypes.string,
	customGroupHeaderRender: PropTypes.func,
	sortData: PropTypes.func.isRequired,
	rowSize: PropTypes.oneOf(['large', 'small']),
	stickyColumns: PropTypes.number,
	collapsedGroups: PropTypes.objectOf(PropTypes.bool).isRequired,
	toggleGroupCollapse: PropTypes.func.isRequired,
};

EmptyLayer.propTypes = {
	emptyMessage: PropTypes.string.isRequired,
};

MultiLayerTable.defaultProps = {
	emptyMessage: "No data found",
};

export default MultiLayerTable;
